import { useEffect, useRef, useState } from 'react';
import { Minus, X } from 'lucide-react';
import { RbChatMessageList } from '../components/RbChatMessageList';
import type { PageContext } from './usePageContext';
import type { WidgetMessage } from './useChatWidget';

interface FloatingPanelProps {
  messages: WidgetMessage[];
  streamingContent: string;
  isStreaming: boolean;
  pageContext: PageContext | null;
  error: string | null;
  onMinimize: () => void;
  onClose: () => void;
  onSend: (content: string) => void;
}

function pageContextLabel(pageContext: PageContext | null): string | null {
  if (!pageContext) return null;
  const noun = pageContext.type === 'job' ? 'Job' : 'Candidate';
  return `${noun} #${pageContext.id}`;
}

export function FloatingPanel({ messages, streamingContent, isStreaming, pageContext, error, onMinimize, onClose, onSend }: FloatingPanelProps) {
  const [input, setInput] = useState('');
  const label = pageContextLabel(pageContext);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, streamingContent]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    onSend(input);
    setInput('');
  };

  return (
    <div
      role="dialog"
      aria-label="Ask RB"
      className="fixed bottom-5 right-5 z-30 flex h-[520px] w-96 max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-2xl"
    >
      <header className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-4 py-3">
        <div>
          <span className="font-semibold">Ask RB</span>
          {label && <p className="text-xs text-gray-500 dark:text-gray-400">Now viewing: {label}</p>}
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={onMinimize} aria-label="Minimize" className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800">
            <Minus size={16} />
          </button>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={16} />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <RbChatMessageList messages={messages} streamingContent={streamingContent} />
        {error && <p className="px-4 text-sm text-red-400">{error}</p>}
        <div ref={bottomRef} />
      </main>

      <form onSubmit={handleSubmit} className="flex gap-2 border-t border-gray-200 dark:border-gray-800 p-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about a candidate or job..."
          className="flex-1 rounded-lg bg-white dark:bg-gray-800 px-3 py-2 text-sm outline-none"
          disabled={isStreaming}
        />
        <button type="submit" disabled={isStreaming || !input.trim()} className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium disabled:opacity-50">
          Send
        </button>
      </form>
    </div>
  );
}
